/**
 * Канбан выбранной доски. Макет data/Задачник.dc.html.
 *
 * Один экран обслуживает оба режима (§8.3): колонки-этапы для ОА и ООА,
 * колонки-проекты для ОАП. Различие живёт в данных доски, а не в двух
 * разных страницах — этого требует §0.3.
 *
 * Панель задачи открывается поверх доски, но URL отражает открытую задачу
 * (§3.4): по ссылке /boards/:id/tasks/:key экран восстанавливается целиком.
 */

import { Permission, ProjectBindingMode, WatchTargetType } from "@taskflow/contracts";
import { Info } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import { useMidnightRefresh } from "@/shared/api/useMidnightRefresh";
import { usePermission, useTopics, type WsError } from "@taskflow/client-core";
import { useIsPhone } from "@/shared/lib/media";
import {
  ConfirmDialog,
  ErrorState,
  KanbanBoard,
  type MenuAction,
  SkeletonRows,
  toast,
} from "@/shared/ui";
import {
  type KanbanColumnData,
  type MoveIntent,
  useBoardMembersQuery,
  useBoardQuery,
  useBoardTagsQuery,
} from "@taskflow/client-core/boards";
import { Archive, Folder, Pencil } from "@/shared/ui/motionIcons";

import {
  filtersToQuery,
  useArchiveColumnTasksMutation,
  useMoveTaskMutation,
  useTasksQuery,
} from "@taskflow/client-core/tasks";
import { CreateTaskDialog } from "../tasks/CreateTaskDialog";
import { PhoneTaskbookBar } from "../tasks/PhoneTaskbookBar";
import { TaskFilterBar } from "../tasks/TaskFilterBar";
import { useTaskFilters } from "../tasks/useTaskFilters";
import { usePrioritiesQuery, useTaskTypesQuery } from "@taskflow/client-core/admin";
import { ArchivedBoardNotice } from "./BoardArchiveDialogs";
import { BoardHeader } from "./BoardHeader";
import { buildKanbanColumns, useDepartmentTabs } from "@taskflow/client-core/boards";
import { rememberBoardId } from "./lastBoard";
import { RenameColumnDialog } from "./RenameColumnDialog";
import { ProjectInfoDialog } from "./ProjectInfoDialog";
import { ColumnDialog } from "./settings/ColumnsSection";
import { useWatchMenu } from "../watching/useWatchMenu";

export function BoardPage() {
  const { boardId = "" } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Задачи, созданные и перемещённые другими участниками, — без перезагрузки.
  useTopics([boardId === "" ? null : `board:${boardId}`]);

  const board = useBoardQuery(boardId, { skip: boardId === "" });
  // Отметки о просмотре гаснут в 00:00 — доска, открытая с вечера,
  // перезапрашивает задачи сама.
  useMidnightRefresh(
    board.data?.viewMarkEnabled === true &&
      board.data.projectBindingMode === ProjectBindingMode.COLUMN,
  );
  const { filters, validation, activeCount, setFilter, setFilters, reset } = useTaskFilters();
  const phone = useIsPhone();
  const [searchInput, setSearchInput] = useState(filters.search);

  const members = useBoardMembersQuery(boardId, { skip: boardId === "" });
  const boardTags = useBoardTagsQuery(boardId, { skip: boardId === "" });
  // Варианты фильтров — из справочников. Прежде здесь стояли константы
  // с выдуманными идентификаторами («pr-low», «tt-task»): сервер не мог
  // разобрать их как UUID, и фильтр по приоритету и типу не работал.
  const priorities = usePrioritiesQuery({});
  const taskTypes = useTaskTypesQuery({});

  // Фильтры уходят в запрос. Пустой диапазон (§16.3) не отправляется:
  // сервер вернул бы пустой список, а причина осталась бы неясной.
  const tasks = useTasksQuery(
    { boardId, ...filtersToQuery(filters) },
    { skip: boardId === "" || validation.invalidRange },
  );

  const [moveTask] = useMoveTaskMutation();

  // Открытая доска запоминается, чтобы /tasks и вкладка её подразделения
  // вели именно сюда (§3.4, §8.5).
  if (boardId !== "") rememberBoardId(boardId, board.data?.departmentCode);

  const boardPermissions = board.data?.permissions ?? [];
  const canMove = usePermission(
    [Permission.TASK_MOVE_OWN, Permission.TASK_MOVE_ALL],
    boardPermissions,
  );
  const canCreateTask = usePermission(Permission.TASK_CREATE, boardPermissions);
  const canEditColumns = usePermission(Permission.BOARD_COLUMNS, boardPermissions);
  const canArchive = usePermission(Permission.TASK_ARCHIVE, boardPermissions);
  const columnMode = board.data?.projectBindingMode === ProjectBindingMode.COLUMN;

  // null — форма закрыта; columnId null — открыта из шапки, без столбца.
  const [creating, setCreating] = useState<{ columnId: string | null } | null>(null);
  const [columnEdit, setColumnEdit] = useState<{
    kind: "rename" | "project";
    columnId: string;
  } | null>(null);

  // Столбец, чья «Информация о проекте» открыта из меню «⋯».
  const [projectInfoColumnId, setProjectInfoColumnId] = useState<string | null>(null);
  // Столбец «Закрыто», задачи которого ждут подтверждения архивации.
  const [archivingColumn, setArchivingColumn] = useState<{ id: string; name: string; count: number } | null>(
    null,
  );
  const [archiveColumnTasks, archiveColumnState] = useArchiveColumnTasksMutation();
  const watchMenu = useWatchMenu();
  const departmentTabs = useDepartmentTabs();
  // На личной доске наблюдать не за кем: о своих действиях не уведомляют.
  const personal = board.data?.isPersonal === true;

  // Макет: «⋯» столбца — переименование, а в режиме COLUMN ещё и проект.
  // В режиме COLUMN меню есть у всех: «Информация о проекте» открыта
  // каждому участнику, правка в ней — только руководителю доски.
  const columnProjectIds = useMemo(
    () => new Map((board.data?.columns ?? []).map((column) => [column.id, column.projectId])),
    [board.data],
  );
  const columnProjectNames = useMemo(
    () => new Map((board.data?.columns ?? []).map((column) => [column.id, column.projectName])),
    [board.data],
  );
  const closedColumnIds = useMemo(
    () =>
      new Set(
        (board.data?.columns ?? [])
          .filter((column) => column.statusCategory === "CLOSED")
          .map((column) => column.id),
      ),
    [board.data],
  );
  const columnActions = useCallback(
    (column: KanbanColumnData): MenuAction[] => {
      const actions: MenuAction[] = [];
      const projectId = columnProjectIds.get(column.id) ?? null;
      if (columnMode && projectId !== null) {
        actions.push({
          key: "project-info",
          label: "Информация о проекте",
          icon: Info,
          onSelect: () => setProjectInfoColumnId(column.id),
        });
      }
      // Доска с одним проектом: закрытые задачи убираются в архив разом.
      if (!columnMode && canArchive && closedColumnIds.has(column.id)) {
        actions.push({
          key: "archive-tasks",
          label: "Архивировать задачи",
          icon: Archive,
          disabled: column.cards.length === 0,
          onSelect: () =>
            setArchivingColumn({ id: column.id, name: column.name, count: column.cards.length }),
        });
      }
      // Наблюдение — каждому, кто видит доску: прав оно не даёт.
      if (!personal) {
        const projectName = columnProjectNames.get(column.id) ?? null;
        actions.push(
          ...watchMenu.actionsFor(
            { targetType: WatchTargetType.COLUMN, targetId: column.id, name: column.name },
            actions.length > 0,
          ),
          ...(columnMode && projectId !== null && projectName !== null
            ? watchMenu.actionsFor({
                targetType: WatchTargetType.PROJECT,
                targetId: projectId,
                name: projectName,
              })
            : []),
        );
      }
      if (!canEditColumns) return actions;
      actions.push({
        key: "rename",
        separated: actions.length > 0,
        label: "Переименовать столбец",
        icon: Pencil,
        onSelect: () => setColumnEdit({ kind: "rename", columnId: column.id }),
      });
      if (columnMode) {
        actions.push({
          key: "project",
          label: "Изменить проект столбца",
          icon: Folder,
          onSelect: () => setColumnEdit({ kind: "project", columnId: column.id }),
        });
      }
      return actions;
    },
    [
      canArchive,
      canEditColumns,
      closedColumnIds,
      columnMode,
      columnProjectIds,
      columnProjectNames,
      personal,
      watchMenu,
    ],
  );

  // Сборка колонок — общая с приложением (client-core/boards/kanban.ts).
  const columns = useMemo<KanbanColumnData[]>(
    () => (board.data ? buildKanbanColumns(board.data, tasks.data?.data ?? []) : []),
    [board.data, tasks.data],
  );

  const handleMove = useCallback(
    (intent: MoveIntent) => {
      const task = tasks.data?.data.find((item) => item.key === intent.taskKey);
      if (!task) return;

      void moveTask({
        key: intent.taskKey,
        toColumnId: intent.toColumnId,
        position: intent.position,
        afterTaskKey: intent.afterTaskKey,
        version: task.version,
        boardId,
      })
        .unwrap()
        .catch((raw: unknown) => {
          const error = raw as WsError;
          // 409 — не «ошибка сети», а содержательный конфликт: кто-то
          // изменил задачу параллельно. Пользователь должен это понимать.
          if (error.status === 409) {
            // Сообщение обещает свежие данные — значит, их нужно получить:
            // иначе в кэше остаётся устаревшая версия и повтор снова даст 409.
            void tasks.refetch();
            toast.warning(
              "Задача изменилась",
              "Кто-то переместил или отредактировал её параллельно. Данные обновлены.",
            );
          } else {
            toast.error(error);
          }
        });
    },
    [boardId, moveTask, tasks],
  );

  if (board.isLoading) return <SkeletonRows rows={4} className="p-6" />;

  if (board.error !== undefined) {
    const error = board.error as WsError;
    return (
      <ErrorState
        title="Доска недоступна"
        message={error.data.message}
        correlationId={error.data.correlationId}
        onRetry={() => void board.refetch()}
      />
    );
  }

  if (!board.data) return null;

  if (board.data.archivedAt !== null) return <ArchivedBoardNotice board={board.data} />;

  const editedColumn =
    columnEdit === null
      ? undefined
      : board.data.columns.find((column) => column.id === columnEdit.columnId);
  const projectInfoColumn =
    projectInfoColumnId === null
      ? undefined
      : board.data.columns.find((column) => column.id === projectInfoColumnId);

  const departmentLabel =
    departmentTabs.labels[board.data.departmentCode] ?? board.data.departmentCode;

  const boardHeader = (
    <BoardHeader
      board={board.data}
      // На доске с проектом у каждого столбца задача создаётся из столбца:
      // так её проект известен сразу.
      {...(canCreateTask && !columnMode
        ? { onCreateTask: () => setCreating({ columnId: null }) }
        : {})}
    />
  );

  const filterBar = (
    <TaskFilterBar
      filters={filters}
      validation={validation}
      activeCount={activeCount}
      setFilter={setFilter}
      setFilters={setFilters}
      reset={reset}
      searchInput={searchInput}
      onSearchInputChange={setSearchInput}
      assignees={(members.data?.data ?? []).map((m) => ({
        id: m.userId,
        label: `${m.lastName} ${m.firstName}`,
      }))}
      priorities={(priorities.data?.data ?? []).map((item) => ({
        id: item.id,
        label: item.name,
      }))}
      types={(taskTypes.data?.data ?? []).map((item) => ({ id: item.id, label: item.name }))}
      tags={(boardTags.data?.data ?? [])
        .filter((tag) => tag.kind === "REGULAR")
        .map((tag) => ({ id: tag.id, label: tag.name }))}
    />
  );

  return (
    <div className="flex h-[calc(100dvh-var(--header-h)-var(--tabbar-h))] flex-col">
      {phone ? (
        // На телефоне шапка и фильтры свёрнуты; смена доски сворачивает
        // их снова — выбор сделан, место нужно канбану.
        <PhoneTaskbookBar
          key={board.data.id}
          title={`${departmentLabel} · ${board.data.name}`}
          activeFilters={activeCount}
          header={boardHeader}
          filters={filterBar}
        />
      ) : (
        <>
          {boardHeader}
          {filterBar}
        </>
      )}

      {validation.invalidRange && (
        <p role="alert" className="bg-danger-soft px-4 py-2 text-[13px] font-semibold text-danger">
          Дата «с» позже даты «по» — задайте корректный период, чтобы увидеть задачи
        </p>
      )}

      {tasks.error !== undefined ? (
        <ErrorState
          message={(tasks.error as WsError).data.message}
          onRetry={() => void tasks.refetch()}
        />
      ) : tasks.isLoading ? (
        <SkeletonRows rows={5} className="p-6" />
      ) : (
        <div className="min-h-0 flex-1">
          <KanbanBoard
            columns={columns}
            canMove={canMove}
            onMove={handleMove}
            onMoveRejected={(reason) => toast.warning("Перемещение невозможно", reason)}
            {...(canCreateTask
              ? { onCreateTask: (columnId: string) => setCreating({ columnId }) }
              : {})}
            columnActions={columnActions}
            onOpenTask={(key) =>
              // §25.5: фильтры в адресе переживают открытие карточки.
              void navigate({ pathname: `/boards/${boardId}/tasks/${key}`, search: location.search })
            }
          />
        </div>
      )}

      {creating !== null && (
        <CreateTaskDialog
          board={board.data}
          initialColumnId={creating.columnId}
          onClose={() => setCreating(null)}
        />
      )}
      {editedColumn !== undefined && columnEdit?.kind === "rename" && (
        <RenameColumnDialog
          board={board.data}
          column={editedColumn}
          onClose={() => setColumnEdit(null)}
        />
      )}
      {editedColumn !== undefined && columnEdit?.kind === "project" && (
        <ColumnDialog board={board.data} column={editedColumn} onClose={() => setColumnEdit(null)} />
      )}

      {projectInfoColumn !== undefined && (
        <ProjectInfoDialog
          boardId={board.data.id}
          column={projectInfoColumn}
          canEdit={canEditColumns}
          onClose={() => setProjectInfoColumnId(null)}
        />
      )}

      <ConfirmDialog
        open={archivingColumn !== null}
        title="Архивировать задачи"
        description={
          archivingColumn === null
            ? ""
            : `Все задачи столбца «${archivingColumn.name}» (${String(archivingColumn.count)}) будут перемещены в архив. Вернуть их можно из архива доски.`
        }
        confirmLabel="Архивировать"
        loading={archiveColumnState.isLoading}
        onConfirm={() => {
          if (archivingColumn === null) return;
          const { id, name } = archivingColumn;
          void archiveColumnTasks({
            boardId,
            columnId: id,
            reason: `Архивация задач столбца «${name}»`,
          })
            .unwrap()
            .then(
              (result) => {
                setArchivingColumn(null);
                toast.success("Задачи перемещены в архив", `Архивировано: ${String(result.archived)}`);
              },
              (raw: unknown) => toast.error(raw as WsError, "Не удалось архивировать задачи"),
            );
        }}
        onCancel={() => setArchivingColumn(null)}
      />

      {watchMenu.dialog}

      {/* Панель задачи открывается поверх доски: доска остаётся смонтированной. */}
      <Outlet />
    </div>
  );
}
